import { toast } from '@wellesley/system-toast'
import { isNumber } from 'lodash/fp'
import { QueryTags } from '@wellesley/openapi/queryTags'
import { getErrorMsg } from '@wellesley/error-kit'
import { type StoreWithUser } from '@wellesley/user'
import { userSlice } from '@wellesley/user/slices/userSlice'
import { type AppDispatch } from '@wellesley/store'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import {
  api,
  GetApiV1PostsApiArg,
  GetApiV1PostsFeedsPublicApiArg,
  GetApiV1PostsFeedsFollowingApiArg,
} from '@wellesley/openapi'

import findFeed from '../feedsSlice/helpers/findFeed'
import getReduxKey from '../feedsSlice/helpers/getReduxKey'
import getPostPageKey from '../feedsSlice/helpers/getPostPageKey'
import { REPLIES_KEY } from '../feedsSlice/helpers/isRepliesPage'
import { PUBLIC_FEEDS_KEY } from '../feedsSlice/helpers/isPublicFeeds'
import {
  Post,
  FEEDS_KEY,
  QUOTES_KEY,
  FOLLOWING_KEY,
  REPOSTS_KEY,
  COMMENTS_KEY,
  BOOKMARKS_KEY,
  feedsSlice,
  FeedsSliceState,
} from '../feedsSlice'

const getPostsPrefix = (args: GetApiV1PostsApiArg) => {
  const isQuotesPosts = args.repostKind === 'QUOTE'
  const isRepostsPosts = args.repostKind === 'REPOST' || args.repostsOnly

  switch (true) {
    case isQuotesPosts:
      return QUOTES_KEY
    case isRepostsPosts:
      return REPOSTS_KEY
    default:
      return FEEDS_KEY
  }
}

const INITIAL_DATA_KEYS = {
  feed: 'feed',
  post: 'post',
}

const updateUserPostsCount = (
  dispatch: AppDispatch,
  store: StoreWithUser,
  delta: number,
) => {
  const currentUser = userSlice.selectors.selectCurrentUser(store)
  if (!currentUser) return

  dispatch(
    api.util.updateQueryData(
      'getApiV1Users',
      { username: currentUser.username, full: true, htmlContent: true },
      (draft) => {
        const user = draft as unknown as { stats?: { posts?: number } }
        if (user.stats) {
          user.stats.posts = Math.max((user.stats.posts || 0) + delta, 0)
        }
      },
    ),
  )
}

export const feedsApi = api.enhanceEndpoints({
  endpoints: {
    getApiV1PostsReplies: {
      forceRefetch: () => true,
      serializeQueryArgs: ({ endpointName }) => endpointName,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const posts = await queryFulfilled

          dispatch(
            feedsSlice.actions.addList({
              key: getReduxKey(REPLIES_KEY, args.userId as string),
              posts: posts.data,
              isNewList: !args.lastId,
            }),
          )
        } catch (e) {
          toast.error(
            `Failed to fetch replies ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1Posts: {
      extraOptions: { maxRetries: 0, initialDataKey: INITIAL_DATA_KEYS.feed },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        queryArgs.pinned
          ? `${endpointName} ${queryArgs.pinned}`
          : `${endpointName} ${queryArgs.repostKind}`,
      forceRefetch: () => true,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const posts = await queryFulfilled

          dispatch(
            feedsSlice.actions.addList({
              key: getReduxKey(
                getPostsPrefix(args),
                (args.userId || args.repostOf || args.ownerId) as string,
              ),
              posts: posts.data,
              isNewList: isNumber(args.offset)
                ? args.offset === 0
                : !args.lastId,
            }),
          )
        } catch (e) {
          toast.error(
            `Failed to fetch posts ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1PostsFeedsPublic: {
      extraOptions: {
        initialDataKey: INITIAL_DATA_KEYS.feed,
        validateInitialDataParams: ({
          requestArgs: { params },
        }: {
          requestArgs: {
            params: GetApiV1PostsFeedsPublicApiArg
          }
        }) => {
          return Boolean(
            params.includeBookmarkStatus &&
              params.withRepostedBy &&
              params.withTranslation &&
              params.includePublisher &&
              !params.skipReposts &&
              !params.withTrends &&
              !params.includeFollowStatus,
          )
        },
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      forceRefetch: () => true,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const posts = await queryFulfilled

          dispatch(
            feedsSlice.actions.addList({
              key: PUBLIC_FEEDS_KEY,
              posts: posts.data,
              isNewList: !args.lastId,
            }),
          )
        } catch (e: unknown) {
          if ((e as { error: Error }).error.name === 'AbortError') return

          toast.error(
            `Failed to fetch posts ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1PostsFeedsFollowing: {
      extraOptions: {
        initialDataKey: INITIAL_DATA_KEYS.feed,
        validateInitialDataParams: ({
          requestArgs: { params },
        }: {
          requestArgs: {
            params: GetApiV1PostsFeedsFollowingApiArg
          }
        }) => {
          return Boolean(
            params.includeBookmarkStatus &&
              params.withRepostedBy &&
              params.withTranslation &&
              params.includePublisher &&
              !params.includeFollowStatus,
          )
        },
      },
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        try {
          const response = await queryFulfilled

          const store = getState() as unknown as StoreWithUser
          const userId = store.user.data?.selectedUserId || ''

          dispatch(
            feedsSlice.actions.addList({
              key: getReduxKey(FOLLOWING_KEY, userId),
              posts: response.data,
              isNewList: !args.lastId,
            }),
          )
        } catch (e) {
          toast.error(
            `Failed to fetch following posts ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1PostsBookmarked: {
      extraOptions: { initialDataKey: INITIAL_DATA_KEYS.feed },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      forceRefetch: () => true,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled

          dispatch(
            feedsSlice.actions.addList({
              key: getReduxKey(BOOKMARKS_KEY),
              posts: response.data,
              isNewList: isNumber(args.offset)
                ? args.offset === 0
                : !args.lastId,
            }),
          )
        } catch (e) {
          toast.error(
            `Failed to fetch bookmarks ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1Posts: {
      invalidatesTags: ['Profile'],
      async onQueryStarted(
        _requestArgs,
        { dispatch, getState, queryFulfilled },
      ) {
        const toastId = 'posting-post'
        toast.loading('Posting...', { id: toastId })

        try {
          const post = await queryFulfilled
          toast.dismiss(toastId)
          dispatch(feedsSlice.actions.addPost(post.data))

          const store = getState() as unknown as StoreWithUser
          updateUserPostsCount(dispatch, store, 1)

          toast.success('Posted')
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to create post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    putApiV1PostsByPostId: {
      invalidatesTags: ['Post'],
      async onQueryStarted(_requestArgs, { dispatch, queryFulfilled }) {
        const toastId = 'updating-post'
        toast.loading('Updating...', { id: toastId })

        try {
          const { data } = await queryFulfilled
          dispatch(feedsSlice.actions.updatePost(data))
          toast.dismiss(toastId)
          toast.success('Updated')
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to update post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    deleteApiV1PostsByPostId: {
      invalidatesTags: ['Profile'],
      async onQueryStarted({ postId }, { dispatch, getState, queryFulfilled }) {
        const toastId = 'deleting-post'
        toast.loading('Deleting...', { id: toastId })

        try {
          await queryFulfilled
          toast.dismiss(toastId)
          toast.success('Deleted')
          dispatch(feedsSlice.actions.deletePost({ postId }))

          const store = getState() as unknown as StoreWithUser
          updateUserPostsCount(dispatch, store, -1)
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to delete post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1PostsByPostId: {
      extraOptions: { maxRetries: 0, initialDataKey: INITIAL_DATA_KEYS.post },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          const feed = await queryFulfilled
          const postId = params.postId

          dispatch(
            feedsSlice.actions.addList({
              key: getPostPageKey({
                postId,
                maxLevel: params.maxLevel,
                withComments: params.withComments,
              }),
              posts: [feed.data],
              isNewList: true,
            }),
          )
        } catch (e) {
          toast.error(
            `Failed to fetch post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
      keepUnusedDataFor: 1,
    },
    getApiV1PostsByPostIdComments: {
      providesTags: [QueryTags.COMMENTS],
      serializeQueryArgs: ({ endpointName }) => endpointName,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const posts = await queryFulfilled

          dispatch(
            feedsSlice.actions.addList({
              key: getReduxKey(COMMENTS_KEY, args.postId),
              posts: posts.data,
              isNewList: !args.lastId,
            }),
          )
        } catch (e) {
          toast.error(
            `Failed to fetch comments ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1PostsByPostIdComments: {},
    putApiV1PostsByPostIdCommentsAndCommentId: {
      async onQueryStarted(_requestArgs, { dispatch, queryFulfilled }) {
        const toastId = 'updating-comment'
        toast.loading('Updating...', { id: toastId })

        try {
          const { data } = await queryFulfilled

          dispatch(feedsSlice.actions.updatePost(data))

          toast.dismiss(toastId)
          toast.success('Updated')
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to update comment ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    deleteApiV1PostsByPostIdCommentsAndCommentId: {
      invalidatesTags: [QueryTags.COMMENTS],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const toastId = 'deleting-comment'
        toast.loading('Deleting...', { id: toastId })

        try {
          await queryFulfilled
          dispatch(
            feedsSlice.actions.deleteComment({
              ...args,
              skipCounterUpdate: true,
            }),
          )
          toast.dismiss(toastId)
          toast.success('Deleted')
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to delete comment ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1PostsByPostIdLikes: {
      invalidatesTags: [QueryTags.LIKES],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const incrementLikes = (count?: number, isIncrement = true) =>
          dispatch(
            feedsSlice.actions.likePost({
              id: args.postId,
              count,
              isLiked: true,
              isIncrement,
            }),
          )

        incrementLikes()
        try {
          await queryFulfilled
        } catch (e) {
          incrementLikes(undefined, false)
          toast.error(
            `Failed to like post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    deleteApiV1PostsByPostIdLikes: {
      invalidatesTags: [QueryTags.LIKES],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const decrementLikes = (count?: number, isIncrement = false) =>
          dispatch(
            feedsSlice.actions.likePost({
              id: args.postId,
              count,
              isLiked: false,
              isIncrement,
            }),
          )

        decrementLikes()
        try {
          await queryFulfilled
        } catch (e) {
          decrementLikes(undefined, true)
          toast.error(
            `Failed to dislike post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1PostsByPostIdRepost: {
      invalidatesTags: [QueryTags.REPOSTS],
      async onQueryStarted(_args, { dispatch, getState, queryFulfilled }) {
        const toastId = 'reposting-post'
        toast.loading('Reposting...', { id: toastId })

        try {
          const post = await queryFulfilled
          toast.dismiss(toastId)
          toast.success('Reposted')

          const store = getState() as unknown as StoreWithUser
          const userId = store.user.data?.selectedUserId || ''

          dispatch(feedsSlice.actions.repost({ userId, post: post.data }))
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to repost post ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1PostsPinsByPostId: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const store = getState() as unknown as { feeds: FeedsSliceState }

        const keys = Object.keys(store.feeds.data)

        let isPinnedPost = true

        for (const key of keys) {
          if (!isPinnedPost) break

          const feed = findFeed(args.postId, {
            id: 'root-feed',
            replies: store.feeds.data[key].items,
          } as Post)

          if (feed && feed.path.length > 0) {
            isPinnedPost = false
            break
          }
        }

        const toastId = 'pinning-post'
        toast.loading(isPinnedPost ? 'Pinning...' : 'Highlighting...', {
          id: toastId,
        })

        try {
          await queryFulfilled
          toast.dismiss(toastId)

          if (isPinnedPost) {
            toast.success('Post pinned')
          } else {
            const onViewReply = () => {
              const reply = document.getElementById(`reply-feed-${args.postId}`)
              reply?.scrollIntoView({ behavior: 'smooth' })
            }
            toast.success('Reply highlighted.', {
              actionButton: {
                label: 'View',
                onClick: onViewReply,
              },
            })
          }

          dispatch(
            feedsSlice.actions.updatePinPost({
              postId: args.postId,
              pinned: true,
              isComment: !isPinnedPost,
            }),
          )
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to pin post. ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    deleteApiV1PostsPinsByPostId: {
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        const store = getState() as unknown as { feeds: FeedsSliceState }
        const keys = Object.keys(store.feeds.data)

        let isPinnedPost = true

        for (const key of keys) {
          if (!isPinnedPost) break

          const feed = findFeed(args.postId, {
            id: 'root-feed',
            replies: store.feeds.data[key].items,
          } as Post)

          if (feed && feed.path.length > 0) {
            isPinnedPost = false
            break
          }
        }

        const toastId = 'removing-pin-post'
        toast.loading(
          isPinnedPost ? 'Removing pin...' : 'Removing highlight...',
          { id: toastId },
        )

        try {
          await queryFulfilled
          toast.dismiss(toastId)
          toast.success(
            isPinnedPost ? 'Removed pin' : 'Highlight removed from reply',
          )

          dispatch(
            feedsSlice.actions.updatePinPost({
              postId: args.postId,
              pinned: false,
              isComment: !isPinnedPost,
            }),
          )
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to unpin post. ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1AccountsProfile: {
      providesTags: ['Profile'],
    },
    deleteApiV1PostsByPostIdRepost: {
      invalidatesTags: [QueryTags.REPOSTS],
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        const toastId = 'deleting-repost'
        toast.loading('Deleting...', { id: toastId })

        const store = getState() as unknown as StoreWithUser
        const userId = store.user.data?.selectedUserId || ''

        try {
          await queryFulfilled
          toast.dismiss(toastId)
          toast.success('Repost deleted')

          dispatch(
            feedsSlice.actions.deleteRepost({
              userId,
              postId: args.postId,
            }),
          )
        } catch (e) {
          toast.dismiss(toastId)
          toast.error(
            `Failed to delete repost. ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    getApiV1PostsByPostIdLikes: {
      providesTags: [QueryTags.LIKES],
    },
    getApiV1PostsByPostIdReposts: {
      providesTags: [QueryTags.REPOSTS],
    },
    getApiV1PostsByPostIdCommenters: {
      providesTags: [QueryTags.COMMENTS],
    },
  },
})

export const {
  useGetApiV1PostsQuery,
  useGetApiV1PostsPinsQuery,
  useLazyGetApiV1PostsQuery,
  usePostApiV1PostsMutation,
  useGetApiV1PostsFeedsPublicQuery,
  useLazyGetApiV1PostsFeedsPublicQuery,
  useGetApiV1PostsRepliesQuery,
  useGetApiV1PostsByPostIdQuery,
  useLazyGetApiV1PostsFeedsFollowingQuery,
  useGetApiV1PostsBookmarkedQuery,
  usePutApiV1PostsByPostIdMutation,
  useLazyGetApiV1PostsByPostIdQuery,
  useGetApiV1PostsByPostIdLikesQuery,
  useDeleteApiV1PostsByPostIdMutation,
  useGetApiV1PostsByPostIdCommentsQuery,
  useLazyGetApiV1PostsByPostIdCommentsQuery,
  usePostApiV1PostsByPostIdLikesMutation,
  usePostApiV1PostsByPostIdRepostMutation,
  useGetApiV1PostsByPostIdCommentersQuery,
  useDeleteApiV1PostsByPostIdLikesMutation,
  usePostApiV1PostsByPostIdCommentsMutation,
  usePutApiV1PostsByPostIdCommentsAndCommentIdMutation,
  useDeleteApiV1PostsByPostIdCommentsAndCommentIdMutation,
} = feedsApi
